/**
 * Secure Storage wrapper
 *
 * Uses expo-secure-store for auth tokens (encrypted) on native.
 * Falls back to localStorage on web.
 */

import { Platform } from 'react-native';
import { normalizeServerUrl } from './constants';

// Web fallback using localStorage
const webStore = {
  getItemAsync: async (key: string): Promise<string | null> => {
    try { return localStorage.getItem(key); } catch { return null; }
  },
  setItemAsync: async (key: string, value: string): Promise<void> => {
    try { localStorage.setItem(key, value); } catch {}
  },
  deleteItemAsync: async (key: string): Promise<void> => {
    try { localStorage.removeItem(key); } catch {}
  },
};

// Use expo-secure-store on native, localStorage on web
const store: {
  getItemAsync: (key: string) => Promise<string | null>;
  setItemAsync: (key: string, value: string) => Promise<void>;
  deleteItemAsync: (key: string) => Promise<void>;
} = Platform.OS === 'web'
  ? webStore
  : require('expo-secure-store');

const KEYS = {
  ACCESS_TOKEN: 'fm_access_token',
  REFRESH_TOKEN: 'fm_refresh_token',
  USER_DATA: 'fm_user_data',
  LANGUAGE: 'fm_language',
  DEVICE_ID: 'fm_device_id',
  SERVER_URL: 'fm_server_url',
} as const;

export async function getAccessToken(): Promise<string | null> {
  return store.getItemAsync(KEYS.ACCESS_TOKEN);
}

export async function setAccessToken(token: string): Promise<void> {
  return store.setItemAsync(KEYS.ACCESS_TOKEN, token);
}

export async function getRefreshToken(): Promise<string | null> {
  return store.getItemAsync(KEYS.REFRESH_TOKEN);
}

export async function setRefreshToken(token: string): Promise<void> {
  return store.setItemAsync(KEYS.REFRESH_TOKEN, token);
}

export async function getUserData(): Promise<any | null> {
  const data = await store.getItemAsync(KEYS.USER_DATA);
  if (!data) return null;

  try {
    return JSON.parse(data);
  } catch {
    await store.deleteItemAsync(KEYS.USER_DATA);
    return null;
  }
}

export async function setUserData(user: any): Promise<void> {
  return store.setItemAsync(KEYS.USER_DATA, JSON.stringify(user));
}

export async function getLanguage(): Promise<string | null> {
  return store.getItemAsync(KEYS.LANGUAGE);
}

export async function setLanguage(lang: string): Promise<void> {
  return store.setItemAsync(KEYS.LANGUAGE, lang);
}

export async function getDeviceId(): Promise<string> {
  let id = await store.getItemAsync(KEYS.DEVICE_ID);
  if (!id) {
    const segments = Array.from({ length: 4 }, () => Math.random().toString(36).slice(2, 10));
    id = `dev_${segments.join('')}_${Date.now().toString(36)}`;
    await store.setItemAsync(KEYS.DEVICE_ID, id);
  }
  return id;
}

export async function getServerUrl(): Promise<string | null> {
  return store.getItemAsync(KEYS.SERVER_URL);
}

export async function setServerUrl(url: string | null): Promise<void> {
  const normalized = normalizeServerUrl(url);
  if (normalized) {
    return store.setItemAsync(KEYS.SERVER_URL, normalized);
  } else {
    return store.deleteItemAsync(KEYS.SERVER_URL);
  }
}

export async function clearAll(): Promise<void> {
  await Promise.all([
    store.deleteItemAsync(KEYS.ACCESS_TOKEN),
    store.deleteItemAsync(KEYS.REFRESH_TOKEN),
    store.deleteItemAsync(KEYS.USER_DATA),
  ]);
}
